/**
 * Shared UI section showing the next activity assignment with optional override toggle.
 * @agentic connections
 */

import { Group, Skeleton, Stack, Switch, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";
import { PractitionerRoleSearchSelect } from "@benchmark/ui/components/atomic";
import { useBenchmarkStaffSearch } from "@/hooks/shared/useFHIROptions";
import { CONFIG } from "@/app/config";

/**
 * Props for NextTaskOwnerSection.
 * @agentic connections
 */
type NextTaskOwnerSectionProps = {
  nextActivityLabel?: string;
  defaultOwner?: { ref: string; display: string };
  appliedRule?: AppliedBusinessRule;
  overrideOwner?: { ref: string; display: string } | null;
  onOverrideChange: (owner: { ref: string; display: string } | null) => void;
  isResolving?: boolean;
};

/**
 * Builds a human-readable explanation when a business rule overrode the default assignment.
 * @agentic connections
 */
const buildRuleExplanation = (
  rule: AppliedBusinessRule,
  ownerDisplay: string | undefined,
  nextActivityLabel: string | undefined,
): string => {
  const activityName = nextActivityLabel ?? "the next activity";
  const ownerName = ownerDisplay ?? "the primary advocate";

  if (rule.rule === "clinician-contact-only") {
    return `This member has a Clinician Contact Only flag. ${activityName} would normally be sent to the unassigned queue, but will instead be assigned to ${ownerName}.`;
  }

  return "";
};

/**
 * Builds the assignment description fragment based on current owner state.
 * @agentic connections
 */
const AssignmentDescription = ({
  displayOwner,
}: {
  displayOwner: string | undefined;
}) => {
  if (displayOwner) {
    return (
      <>
        assigned to{" "}
        <Text span fw={600}>
          {displayOwner}
        </Text>
      </>
    );
  }
  return (
    <Text span fw={600}>
      placed in the unassigned queue
    </Text>
  );
};

/**
 * Displays the next activity assignment in natural language with optional override.
 * @agentic connections
 */
export const NextTaskOwnerSection = ({
  nextActivityLabel,
  defaultOwner,
  appliedRule,
  overrideOwner,
  onOverrideChange,
  isResolving = false,
}: NextTaskOwnerSectionProps) => {
  const [overrideEnabled, setOverrideEnabled] = useState(!!overrideOwner);
  const [searchQuery, setSearchQuery] = useState("");

  const { options, resources, isSearching } = useBenchmarkStaffSearch(
    searchQuery,
    CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
  );

  const handleToggle = useCallback(
    (checked: boolean) => {
      setOverrideEnabled(checked);
      if (!checked) {
        onOverrideChange(null);
        setSearchQuery("");
      }
    },
    [onOverrideChange],
  );

  const handleSelectChange = useCallback(
    (value: string | null) => {
      if (value) {
        const role = resources.find((r) => r.id === value);
        onOverrideChange({
          ref: value,
          display: role?.practitioner?.display || "Staff Member",
        });
      } else {
        onOverrideChange(null);
      }
    },
    [resources, onOverrideChange],
  );

  const isOverrideEnabled = overrideEnabled || !!overrideOwner;
  const displayOwner = overrideOwner?.display ?? defaultOwner?.display;
  const ruleExplanation =
    appliedRule && !overrideOwner
      ? buildRuleExplanation(appliedRule, displayOwner, nextActivityLabel)
      : undefined;

  return (
    <Stack gap="md">
      <Text size="sm" fw={500}>
        Next Activity
      </Text>

      {isResolving ? (
        <Skeleton height={20} width="80%" />
      ) : nextActivityLabel ? (
        <Text size="sm">
          The next activity for this member will be{" "}
          <Text span fw={600}>
            {nextActivityLabel}
          </Text>
          , and it will be <AssignmentDescription displayOwner={displayOwner} />
          .
        </Text>
      ) : (
        <Text size="sm" c="dimmed">
          There are no remaining activities in this care plan.
        </Text>
      )}

      {ruleExplanation && (
        <Group gap={6} wrap="nowrap" align="flex-start">
          <IconInfoCircle
            size={14}
            color="var(--brand-fill)"
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Text size="xs" c="dimmed" fs="italic">
            {ruleExplanation}
          </Text>
        </Group>
      )}

      {nextActivityLabel && (
        <>
          <Group
            justify="space-between"
            align="flex-start"
            wrap="nowrap"
            gap="lg"
          >
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Override assignment
              </Text>
              <Text size="xs" c="dimmed">
                Manually select who the next activity is assigned to
              </Text>
            </Stack>
            <Switch
              aria-label="Override assignment"
              checked={isOverrideEnabled}
              onChange={(e) => handleToggle(e.currentTarget.checked)}
              styles={{ track: { flexShrink: 0 } }}
            />
          </Group>

          {isOverrideEnabled && (
            <PractitionerRoleSearchSelect
              options={options}
              value={overrideOwner?.ref ?? null}
              searchQuery={searchQuery}
              isSearching={isSearching}
              onChange={handleSelectChange}
              onSearchChange={setSearchQuery}
              placeholder="Search staff..."
              noResultsMessage="No matching staff found"
            />
          )}
        </>
      )}
    </Stack>
  );
};
